import React, { useEffect } from 'react';
import { Form, Card, Space, Avatar } from 'antd';
import FormItem from '@/common/FormItem';
import styles from '@/common/Layout/index.module.less';
import CustomTable from 'common/Content/CustomTable';
import CustomButton from '@/common/CustomButton';
import { history } from 'app/history';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserMatrix } from '../../redux/slice';
import { roles } from 'utilities/helpers';
import Loader from '@/common/Loader';

const UserMatrix = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParam = location.search;
  const searchParams = new URLSearchParams(searchParam);
  const role = searchParams.get('role') || '';
  const level = parseInt(searchParams.get('level') || '0', 10);
  const { userMatrix, getUserMatrixLoading } = useSelector(
    (state) => state.admin
  );

  useEffect(() => {
    if (role && level) {
      const payload = { role, level };
      dispatch(fetchUserMatrix(payload));
    }
  }, [role, level, dispatch, location.search]);

  // Helper to render permission avatars based on code from API (e.g. 'VC', 'VCE')
  const renderPermission = (code) => {
    if (!code) return 'NA';

    return (
      <Space>
        {code.includes('V') && (
          <Avatar
            style={{ backgroundColor: 'blue', verticalAlign: 'middle' }}
            size="small"
          >
            V
          </Avatar>
        )}
        {code.includes('C') && (
          <Avatar
            style={{ backgroundColor: 'Green', verticalAlign: 'middle' }}
            size="small"
          >
            C
          </Avatar>
        )}
        {code.includes('E') && (
          <Avatar
            style={{ backgroundColor: '#FFBF00', verticalAlign: 'middle' }}
            size="small"
          >
            E
          </Avatar>
        )}
        {code.includes('D') && (
          <Avatar
            style={{ backgroundColor: '#dc3545', verticalAlign: 'middle' }}
            size="small"
          >
            D
          </Avatar>
        )}
      </Space>
    );
  };

  // Build transactions array from API response (userMatrix)
  // API returns a single object, but Redux stores it as an array, so get the first element
  const matrix =
    userMatrix && userMatrix.length > 0 ? userMatrix[0] : userMatrix || {};
  const transactions = [
    {
      name: 'Material Receipt',
      permission: renderPermission(matrix.material_receipt)
    },
    {
      name: 'Material Movement',
      permission: renderPermission(matrix.material_movement)
    },
    {
      name: 'Sampling / TRF',
      permission: renderPermission(matrix.sampling_trf)
    },
    {
      name: 'Material Status Update',
      permission: renderPermission(matrix.material_status_update)
    },
    {
      name: 'Dispensing Request',
      permission: renderPermission(matrix.dispensing_request)
    },
    {
      name: 'Dispensing Execution',
      permission: renderPermission(matrix.dispensing_execution)
    },
    {
      name: 'Dispensing Approval',
      permission: renderPermission(matrix.dispensing_approval)
    },
    {
      name: 'Stock Hold / Release',
      permission: renderPermission(matrix.stock_hold_release)
    },
    {
      name: 'Stock Deduction',
      permission: renderPermission(matrix.stock_deduction)
    },
    {
      name: 'Stock Reconciliation',
      permission: renderPermission(matrix.stock_reconciliation)
    },
    {
      name: 'Material Return Request',
      permission: renderPermission(matrix.material_return_request)
    },
    {
      name: 'Code to Code Transfer',
      permission: renderPermission(matrix.code_to_code_transfer)
    },
    {
      name: 'Vendor / Material Master',
      permission: renderPermission(matrix.vendor_material_master)
    },
    {
      name: 'Line Clearance',
      permission: renderPermission(matrix.line_clearance)
    },
    {
      name: 'Material Verification',
      permission: renderPermission(matrix.material_verification)
    },
    {
      name: 'WIP Weighing Labels',
      permission: renderPermission(matrix.wip_weighing_labels)
    },
    {
      name: 'BOM Creation',
      permission: renderPermission(matrix.bom_creation)
    },
    {
      name: 'Equipment Master',
      permission: renderPermission(matrix.equipment_master)
    },
    {
      name: 'EBMR Configuration',
      permission: renderPermission(matrix.ebmr_configuration)
    },
    {
      name: 'EBMR Execution',
      permission: renderPermission(matrix.ebmr_execution)
    },
    {
      name: 'EBMR Event Review',
      permission: renderPermission(matrix.ebmr_event_review)
    },
    {
      name: 'EBMR Approval',
      permission: renderPermission(matrix.ebmr_approval)
    },
    {
      name: 'Electronic Logbooks',
      permission: renderPermission(matrix.electronic_logbooks)
    },
    {
      name: 'Reports',
      permission: renderPermission(matrix.reports)
    },
    {
      name: 'Audit Trail',
      permission: renderPermission(matrix.audit_trail)
    },
    {
      name: 'User Management',
      permission: renderPermission(matrix.user_management)
    }
  ];

  const column1 = [
    {
      title: 'Transactions',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: '100px',
      render: (text) => {
        return <b style={{ fontSize: '12px' }}>{text}</b>;
      }
    },
    {
      title: `Permissions`,
      dataIndex: 'permission',
      key: 'permission',
      ellipsis: true,
      width: '100px'
    }
  ];

  if (getUserMatrixLoading) {
    return <Loader />;
  }

  return (
    <>
      <Form form={form} layout="vertical">
        <Card
          title={<span>Permission Matrix</span>}
          className={`${styles.cardStyles_approver}`}
        >
          <p style={{ color: '#f536a5' }}>
            Role: <b>{roles(role, level)}</b> , Level: <b>{level}</b>
          </p>
          <div className={`${styles.table_row}`}>
            <CustomTable
              columns={column1}
              dataSource={transactions}
              pagination={false}
            />
          </div>
        </Card>
        <br />
        <FormItem className="text-center">
          <CustomButton
            className={styles.inwardButton}
            onClick={() => history.back()}
            type="primary"
          >
            Back
          </CustomButton>
        </FormItem>
      </Form>
    </>
  );
};

export default UserMatrix;
